import { db, generateId, now, Project, ProjectCachePref, SyncQueueItem } from './database';
import { triggerImmediateUpload } from '../sync/syncEngine';
import { getMappingEntriesForProject, getPhotosForMappings, ensurePhotoBlob, updateMappingEntry } from './mappings';
import { getFloorPlansByProject, ensureFloorPlanAsset, updateFloorPlan } from './floorPlans';
import { getStructureEntriesForProject, updateStructureEntry } from './structures';

/**
 * Create a new project
 */
export async function createProject(
  projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'synced' | 'archived' | 'syncEnabled'>
): Promise<Project> {
  const project: Project = {
    ...projectData,
    id: generateId(),
    archived: 0,
    syncEnabled: 0, // Default to metadata-only sync (no photos/mappings downloaded)
    createdAt: now(),
    updatedAt: now(),
    version: 1, // Initial version for conflict detection
    lastModified: now(), // For conflict detection
    synced: 0
  };

  try {
    await db.projects.add(project);

    // Add to sync queue
    const syncItem: SyncQueueItem = {
      id: generateId(),
      operation: 'CREATE',
      entityType: 'project',
      entityId: project.id,
      payload: project,
      timestamp: now(),
      retryCount: 0,
      synced: 0
    };
    await db.syncQueue.add(syncItem);
    triggerImmediateUpload();

    console.log('Project created:', project.id);
    return project;
  } catch (error) {
    console.error('Failed to create project:', error);
    throw error;
  }
}

/**
 * Get a project by ID
 */
export async function getProject(id: string): Promise<Project | undefined> {
  return await db.projects.get(id);
}

/**
 * Get all projects for a user
 */
export async function getProjectsForUser(userId: string): Promise<Project[]> {
  return await db.projects
    .where('ownerId')
    .equals(userId)
    .or('accessibleUsers')
    .equals(userId)
    .sortBy('updatedAt');
}

/**
 * Get all projects (for admin)
 */
export async function getAllProjects(): Promise<Project[]> {
  return await db.projects
    .orderBy('updatedAt')
    .reverse()
    .toArray();
}

/**
 * Update a project
 */
export async function updateProject(
  id: string,
  updates: Partial<Omit<Project, 'id' | 'createdAt'>>
): Promise<Project> {
  const project = await db.projects.get(id);
  if (!project) {
    throw new Error(`Project not found: ${id}`);
  }

  const updatedProject: Project = {
    ...project,
    ...updates,
    updatedAt: now(),
    version: (project.version || 0) + 1,
    lastModified: now(),
    synced: 0,
  };

  try {
    await db.projects.put(updatedProject);

    const existingSyncItem = await db.syncQueue
      .where('entityType')
      .equals('project')
      .and((item) => item.entityId === id && item.synced === 0 && item.operation === 'UPDATE')
      .first();

    if (existingSyncItem) {
      await db.syncQueue.update(existingSyncItem.id, {
        payload: updatedProject,
        timestamp: now(),
      });
    } else {
      const syncItem: SyncQueueItem = {
        id: generateId(),
        operation: 'UPDATE',
        entityType: 'project',
        entityId: id,
        payload: updatedProject,
        timestamp: now(),
        retryCount: 0,
        synced: 0,
      };
      await db.syncQueue.add(syncItem);
    }
    triggerImmediateUpload();

    return updatedProject;
  } catch (error) {
    console.error('Failed to update project:', error);
    throw error;
  }
}

/**
 * Delete a project and all associated mapping entries
 */
export async function deleteProject(id: string): Promise<void> {
  try {
    await db.transaction(
      'rw',
      [
        db.projects,
        db.mappingEntries,
        db.photos,
        db.floorPlans,
        db.floorPlanPoints,
        db.sals,
        db.typologyPrices,
        db.projectCachePrefs,
        db.syncQueue,
      ],
      async () => {
        const mappingEntries = await db.mappingEntries.where('projectId').equals(id).toArray();
        const mappingEntryIds = mappingEntries.map((entry) => entry.id);

        const photos = mappingEntryIds.length > 0
          ? await db.photos.where('mappingEntryId').anyOf(mappingEntryIds).toArray()
          : [];
        const photoIds = photos.map((photo) => photo.id);

        const floorPlans = await db.floorPlans.where('projectId').equals(id).toArray();
        const floorPlanIds = floorPlans.map((floorPlan) => floorPlan.id);

        const floorPlanPoints = floorPlanIds.length > 0
          ? await db.floorPlanPoints.where('floorPlanId').anyOf(floorPlanIds).toArray()
          : [];
        const floorPlanPointIds = floorPlanPoints.map((point) => point.id);

        const sals = await db.sals.where('projectId').equals(id).toArray();
        const salIds = sals.map((sal) => sal.id);

        const typologyPrices = await db.typologyPrices.where('projectId').equals(id).toArray();
        const typologyPriceIds = typologyPrices.map((price) => price.id);

        const queueItems = await db.syncQueue.where('synced').equals(0).toArray();
        const hadPendingProjectCreate = queueItems.some(
          (item) =>
            item.entityType === 'project' &&
            item.entityId === id &&
            item.operation === 'CREATE'
        );

        const queueIdsToRemove = queueItems
          .filter((item) => {
            if (item.entityType === 'project' && item.entityId === id) {
              return true;
            }
            if (item.entityType === 'mapping_entry' && mappingEntryIds.includes(item.entityId)) {
              return true;
            }
            if (
              item.entityType === 'photo' &&
              (photoIds.includes(item.entityId) ||
                mappingEntryIds.includes((item.payload as { mappingEntryId?: string })?.mappingEntryId || ''))
            ) {
              return true;
            }
            if (item.entityType === 'floor_plan' && floorPlanIds.includes(item.entityId)) {
              return true;
            }
            if (
              item.entityType === 'floor_plan_point' &&
              (floorPlanPointIds.includes(item.entityId) ||
                floorPlanIds.includes((item.payload as { floorPlanId?: string })?.floorPlanId || ''))
            ) {
              return true;
            }
            if (item.entityType === 'sal' && salIds.includes(item.entityId)) {
              return true;
            }
            if (item.entityType === 'typology_price' && typologyPriceIds.includes(item.entityId)) {
              return true;
            }
            return false;
          })
          .map((item) => item.id);

        if (queueIdsToRemove.length > 0) {
          await db.syncQueue.bulkDelete(queueIdsToRemove);
        }

        // Accodare DELETE storage per ogni foto già caricata sul server
        for (const photo of photos) {
          if (!photo.uploaded) continue;
          await db.syncQueue.add({
            id: generateId(),
            operation: 'DELETE',
            entityType: 'photo',
            entityId: photo.id,
            payload: {
              id: photo.id,
              mappingEntryId: photo.mappingEntryId,
              storagePath: photo.storagePath,
              thumbnailStoragePath: photo.thumbnailStoragePath,
            },
            timestamp: now(),
            retryCount: 0,
            synced: 0,
          });
        }

        if (photoIds.length > 0) {
          await db.photos.bulkDelete(photoIds);
        }
        if (mappingEntryIds.length > 0) {
          await db.mappingEntries.bulkDelete(mappingEntryIds);
        }
        if (floorPlanPointIds.length > 0) {
          await db.floorPlanPoints.bulkDelete(floorPlanPointIds);
        }
        if (floorPlanIds.length > 0) {
          await db.floorPlans.bulkDelete(floorPlanIds);
        }
        if (salIds.length > 0) {
          await db.sals.bulkDelete(salIds);
        }
        if (typologyPriceIds.length > 0) {
          await db.typologyPrices.bulkDelete(typologyPriceIds);
        }

        await db.projectCachePrefs.delete(id);
        await db.projects.delete(id);

        if (!hadPendingProjectCreate) {
          const syncItem: SyncQueueItem = {
            id: generateId(),
            operation: 'DELETE',
            entityType: 'project',
            entityId: id,
            payload: { id },
            timestamp: now(),
            retryCount: 0,
            synced: 0,
          };
          await db.syncQueue.add(syncItem);
        }
      }
    );

    triggerImmediateUpload();
  } catch (error) {
    console.error('Failed to delete project:', error);
    throw error;
  }
}

/**
 * Rimuove un progetto e tutti i suoi figli da Dexie locale.
 * Da usare quando la cancellazione è già avvenuta lato server (pruning).
 * Non accoda sync items perché il server ha già la versione aggiornata.
 */
export async function pruneProjectLocal(id: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.projects, db.mappingEntries, db.photos, db.floorPlans, db.floorPlanPoints, db.sals, db.typologyPrices, db.syncQueue],
    async () => {
      const mappingEntries = await db.mappingEntries.where('projectId').equals(id).toArray();
      const mappingEntryIds = mappingEntries.map((e) => e.id);
      const photos = mappingEntryIds.length > 0
        ? await db.photos.where('mappingEntryId').anyOf(mappingEntryIds).toArray()
        : [];
      const photoIds = photos.map((p) => p.id);
      const floorPlans = await db.floorPlans.where('projectId').equals(id).toArray();
      const floorPlanIds = floorPlans.map((fp) => fp.id);
      const floorPlanPoints = floorPlanIds.length > 0
        ? await db.floorPlanPoints.where('floorPlanId').anyOf(floorPlanIds).toArray()
        : [];
      const floorPlanPointIds = floorPlanPoints.map((p) => p.id);
      const salIds = (await db.sals.where('projectId').equals(id).toArray()).map((s) => s.id);
      const typologyPriceIds = (await db.typologyPrices.where('projectId').equals(id).toArray()).map((p) => p.id);

      // Rimuovere sync queue items pending per questo progetto e tutti i figli
      const queueItems = await db.syncQueue.where('synced').equals(0).toArray();
      const queueIdsToRemove = queueItems
        .filter((item) => {
          if (item.entityType === 'project' && item.entityId === id) return true;
          if (item.entityType === 'mapping_entry' && mappingEntryIds.includes(item.entityId)) return true;
          if (item.entityType === 'photo' && (photoIds.includes(item.entityId) || mappingEntryIds.includes((item.payload as any)?.mappingEntryId || ''))) return true;
          if (item.entityType === 'floor_plan' && floorPlanIds.includes(item.entityId)) return true;
          if (item.entityType === 'floor_plan_point' && (floorPlanPointIds.includes(item.entityId) || floorPlanIds.includes((item.payload as any)?.floorPlanId || ''))) return true;
          if (item.entityType === 'sal' && salIds.includes(item.entityId)) return true;
          if ((item.entityType as string) === 'typology_price' && typologyPriceIds.includes(item.entityId)) return true;
          return false;
        })
        .map((item) => item.id);

      if (queueIdsToRemove.length > 0) await db.syncQueue.bulkDelete(queueIdsToRemove);
      if (photoIds.length > 0) await db.photos.bulkDelete(photoIds);
      if (mappingEntryIds.length > 0) await db.mappingEntries.bulkDelete(mappingEntryIds);
      if (floorPlanPointIds.length > 0) await db.floorPlanPoints.bulkDelete(floorPlanPointIds);
      if (floorPlanIds.length > 0) await db.floorPlans.bulkDelete(floorPlanIds);
      if (salIds.length > 0) await db.sals.bulkDelete(salIds);
      if (typologyPriceIds.length > 0) await db.typologyPrices.bulkDelete(typologyPriceIds);
      await db.projects.delete(id);
    }
  );
}

/**
 * Search projects by title, client, or address
 */
export async function searchProjects(userId: string, query: string): Promise<Project[]> {
  const lowerQuery = query.toLowerCase();
  const projects = await getProjectsForUser(userId);

  return projects.filter(project =>
    project.title.toLowerCase().includes(lowerQuery) ||
    project.client.toLowerCase().includes(lowerQuery) ||
    project.address.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get unsynced projects
 */
export async function getUnsyncedProjects(): Promise<Project[]> {
  return await db.projects
    .where('synced')
    .equals(0)
    .toArray();
}

export async function getProjectCachePref(projectId: string): Promise<ProjectCachePref | undefined> {
  return db.projectCachePrefs.get(projectId);
}

export async function setProjectOfflinePinned(
  projectId: string,
  offlinePinned: boolean
): Promise<ProjectCachePref> {
  const existing = await db.projectCachePrefs.get(projectId);
  const cachePref: ProjectCachePref = {
    projectId,
    offlinePinned: offlinePinned ? 1 : 0,
    lastHydratedAt: existing?.lastHydratedAt,
    updatedAt: now(),
  };
  await db.projectCachePrefs.put(cachePref);
  return cachePref;
}

async function processInBatches<T>(
  items: T[],
  batchSize: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    await Promise.all(batch.map(worker));
  }
}

export async function hydrateProjectForOffline(projectId: string): Promise<ProjectCachePref> {
  const mappings = await getMappingEntriesForProject(projectId);
  const mappingIds = mappings.map((mapping) => mapping.id);

  if (mappingIds.length > 0) {
    const groupedPhotos = await getPhotosForMappings(mappingIds);
    const allPhotos = Object.values(groupedPhotos).flat();
    await processInBatches(allPhotos, 8, async (photo) => {
      await ensurePhotoBlob(photo.id);
    });
  }

  const floorPlans = await getFloorPlansByProject(projectId);
  for (const floorPlan of floorPlans) {
    await ensureFloorPlanAsset(floorPlan.id, 'thumbnail');
    await ensureFloorPlanAsset(floorPlan.id, 'full');
    await ensureFloorPlanAsset(floorPlan.id, 'pdf');
  }

  const hydratedAt = now();
  const cachePref: ProjectCachePref = {
    projectId,
    offlinePinned: 1,
    lastHydratedAt: hydratedAt,
    updatedAt: hydratedAt,
  };
  await db.projectCachePrefs.put(cachePref);
  return cachePref;
}

/**
 * Mark project as synced
 */
export async function markProjectSynced(id: string): Promise<void> {
  await db.projects.update(id, { synced: 1 });
}

/**
 * Archive a project
 */
export async function archiveProject(id: string): Promise<Project> {
  return await updateProject(id, { archived: 1 });
}

/**
 * Unarchive a project
 */
export async function unarchiveProject(id: string): Promise<Project> {
  return await updateProject(id, { archived: 0 });
}

export interface FloorRenameConflict {
  oldFloor: string;
  newFloor: string;
  reason: 'floor_plan_exists' | 'update_failed';
  detail?: string;
}

/**
 * Calcola la mappa `vecchio nome piano -> nuovo nome piano` confrontando la
 * lista dei piani prima e dopo la modifica.
 *
 * - Stessa lunghezza: confronto posizionale. E' l'unico modo per gestire uno
 *   scambio di nomi tra due piani (es. "0" -> "1" e "1" -> "0").
 * - Lunghezza diversa (piani aggiunti o rimossi nella stessa modifica): il
 *   confronto posizionale sarebbe distruttivo, perche' un inserimento in testa
 *   farebbe scalare tutti i nomi. Accoppiamo quindi solo i piani spariti con
 *   quelli comparsi, nell'ordine in cui compaiono. Se i piani spariti sono piu'
 *   di quelli comparsi siamo di fronte a una cancellazione, non a una rinomina:
 *   in quel caso non tocchiamo nulla. L'accoppiamento resta un'inferenza, quindi
 *   il chiamante deve chiedere conferma all'utente (vedi ProjectForm).
 */
export function computeFloorRenameMap(
  oldFloors: string[],
  newFloors: string[]
): Map<string, string> {
  const renameMap = new Map<string, string>();

  if (oldFloors.length === newFloors.length) {
    for (let i = 0; i < oldFloors.length; i++) {
      if (oldFloors[i] !== newFloors[i]) renameMap.set(oldFloors[i], newFloors[i]);
    }
    return renameMap;
  }

  const removed = oldFloors.filter((f) => !newFloors.includes(f));
  const added = newFloors.filter((f) => !oldFloors.includes(f));
  if (removed.length === 0 || added.length < removed.length) return renameMap;

  for (let i = 0; i < removed.length; i++) {
    renameMap.set(removed[i], added[i]);
  }
  return renameMap;
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * In modalita' online-first le entita' possono essere lette dal remoto senza
 * essere ancora presenti in IndexedDB. Gli helper di update leggono la riga
 * locale e lanciano se non la trovano, quindi la seminiamo prima.
 */
async function ensureLocalRow<T extends { id: string }>(table: any, row: T): Promise<void> {
  const existing = await table.get(row.id);
  if (!existing) await table.put(row);
}

/**
 * Rinomina in batch i piani di un progetto date le vecchie e nuove liste,
 * propagando il nuovo nome a tutte le entita' collegate (planimetrie,
 * mappature, strutture) che referenziano il piano tramite il campo `floor`
 * come stringa letterale. Senza questa cascata, rinominare un piano orfanizza
 * tutti i dati esistenti su quel piano (restano indicizzati col vecchio nome
 * e spariscono dalla UI).
 *
 * Legge lo stato di TUTTE le entita' del progetto in un colpo solo, PRIMA di
 * scrivere qualunque rename: cosi' anche uno scambio di nomi tra due piani
 * nella stessa modifica usa il piano ORIGINALE di ciascuna entita' e non uno
 * gia' riscritto da un rename precedente nello stesso batch.
 *
 * Le planimetrie vengono rinominate PER PRIME e ogni entita' e' aggiornata in
 * modo isolato: un singolo errore non deve piu' interrompere l'intera cascata
 * lasciando planimetrie e punti orfani. Gli errori sono restituiti al chiamante
 * insieme ai conflitti, per poterli mostrare all'utente.
 */
export async function renameProjectFloors(
  projectId: string,
  oldFloors: string[],
  newFloors: string[],
  userId: string
): Promise<FloorRenameConflict[]> {
  const renameMap = computeFloorRenameMap(oldFloors, newFloors);
  if (renameMap.size === 0) return [];

  const [mappingEntries, structureEntries, floorPlans] = await Promise.all([
    getMappingEntriesForProject(projectId),
    getStructureEntriesForProject(projectId),
    getFloorPlansByProject(projectId),
  ]);

  const conflicts: FloorRenameConflict[] = [];

  // 1) Planimetrie per prime: sono l'entita' la cui perdita e' piu' visibile
  // (planimetria + punti annotati collegati) e non deve dipendere dall'esito
  // degli update delle mappature.
  // Un piano di destinazione e' occupato solo se la planimetria che ci sta
  // sopra NON viene a sua volta rinominata: altrimenti libera il posto.
  const targetOccupied = new Set(
    floorPlans.filter((fp) => !renameMap.has(fp.floor)).map((fp) => fp.floor)
  );

  for (const fp of floorPlans) {
    const newFloor = renameMap.get(fp.floor);
    if (!newFloor) continue;
    if (targetOccupied.has(newFloor)) {
      conflicts.push({ oldFloor: fp.floor, newFloor, reason: 'floor_plan_exists' });
      continue;
    }
    try {
      await updateFloorPlan(fp.id, { floor: newFloor });
      targetOccupied.add(newFloor);
    } catch (err) {
      conflicts.push({
        oldFloor: fp.floor,
        newFloor,
        reason: 'update_failed',
        detail: `planimetria ${fp.originalFilename || fp.id}: ${describeError(err)}`,
      });
    }
  }

  // 2) Mappature e strutture: ogni update e' isolato, cosi' una singola voce
  // non aggiornabile non blocca tutte le altre.
  for (const entry of mappingEntries) {
    const newFloor = renameMap.get(entry.floor);
    if (!newFloor) continue;
    try {
      await ensureLocalRow(db.mappingEntries, entry);
      await updateMappingEntry(entry.id, { floor: newFloor }, userId);
    } catch (err) {
      conflicts.push({
        oldFloor: entry.floor,
        newFloor,
        reason: 'update_failed',
        detail: `mappatura ${entry.id}: ${describeError(err)}`,
      });
    }
  }

  for (const entry of structureEntries) {
    const newFloor = renameMap.get(entry.floor);
    if (!newFloor) continue;
    try {
      await ensureLocalRow(db.structureEntries, entry);
      await updateStructureEntry(entry.id, { floor: newFloor }, userId);
    } catch (err) {
      conflicts.push({
        oldFloor: entry.floor,
        newFloor,
        reason: 'update_failed',
        detail: `struttura ${entry.id}: ${describeError(err)}`,
      });
    }
  }

  return conflicts;
}
